import type { FormApi } from '../../form-hooks/useFormApi';
import type { UserRoleFormSubmitFn } from '../../form/UserRoleForm/UserRoleForm';

import { use, useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { twMerge } from 'tailwind-merge';

import { postUserRole } from '../../lib/flotiq-client';
import {
  parseResponseErrors,
  ResponseError,
} from '../../lib/flotiq-client/response-errors';
import { getTestProps } from '../../lib/helpers';

import { useGridNavigate } from '../../components/DataGrid/useGridFilters';
import Heading from '../../components/Heading/Heading';
import LinkButton from '../../components/LinkButton/LinkButton';
import Loader from '../../components/Loader/Loader';
import TopbarBreadcrumbs from '../../components/Topbar/breadcrumbs/TopbarBreadcrumbs';
import TopbarActionMenu from '../../components/Topbar/buttons/base/TopbarActionMenu';
import TopbarCancelButton from '../../components/Topbar/buttons/TopbarCancelButton';
import TopbarDeleteButton from '../../components/Topbar/buttons/TopbarDeleteButton';
import TopbarDuplicateButton from '../../components/Topbar/buttons/TopbarDuplicateButton';
import TopbarSaveAndLeaveButton from '../../components/Topbar/buttons/TopbarSaveAndLeaveButton';
import TopbarSaveButton from '../../components/Topbar/buttons/TopbarSaveButton';

import { useContentTypes, useUserRole } from '../../hooks/api';
import useApiErrorsToast from '../../hooks/api/useApiErrorsToast';
import useFirstLoading from '../../hooks/useFirstLoading';
import useSelectedSpace from '../../hooks/useSelectedSpace';
import useToken from '../../hooks/useToken';

import DirtyHandlerContext from '../../contexts/DirtyHandlerContext';
import { useModals } from '../../contexts/ModalContext';
import UserContext from '../../contexts/UserContext';

import { WarningIcon } from '../../images/shapes';

import UserRoleForm from '../../form/UserRoleForm/UserRoleForm';
import PageLayout, {
  predefinedLayoutClasses,
} from '../../layout/PageLayout/PageLayout';

const testId = 'add-user-role';
interface AddUserRoleProps {
  /**
   * If user role is duplicating
   */
  duplicate?: boolean;
}

const AddUserRole = ({ duplicate = false }: AddUserRoleProps) => {
  const { t } = useTranslation();
  const modal = useModals();
  const jwt = useToken();
  const navigate = useNavigate();
  const navigateOnSave = useRef<boolean>(false);
  const { space, buildUrlWithSpace, spaceLimits } = useSelectedSpace();
  const { id } = useParams();
  const { isAdmin } = use(UserContext);
  const { setDirty } = use(DirtyHandlerContext);

  const { navigateGrid, gridLink } = useGridNavigate(
    'user-roles',
    buildUrlWithSpace('user-roles'),
  );

  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [formState, setFormState] = useState<FormApi>();

  const {
    entity: role,
    isLoading: roleIsLoading,
    updateEntity: putRole,
    deleteEntity: deleteRole,
    errors,
    status,
  } = useUserRole(id);

  const ctdParams = useMemo(
    () => ({
      limit: 1000,
      order_by: 'label',
      order_direction: 'asc',
      page: 1,
    }),
    [],
  );

  const {
    data: contentTypes,
    isLoading: isLoadingCT,
    errors: ctdErrors,
  } = useContentTypes(ctdParams);

  const firstLoading = useFirstLoading(
    (!roleIsLoading || !id) && !isLoadingCT,
    id,
  );

  useApiErrorsToast(errors);
  useApiErrorsToast(ctdErrors);

  const handleDeleteRole = useCallback(async () => {
    modal.deleting('delete-modal');
    try {
      const { body, status, ok } = await deleteRole();
      if (!ok) {
        throw parseResponseErrors(body, status);
      }
      toast.success(t('UserRoles.Deleted'));

      setDirty(false);
      navigateGrid();
    } catch (error) {
      if (!(error instanceof ResponseError)) {
        toast.error(t('Form.CommunicationErrorMessage'));
      } else {
        toast.error(
          error.message ? error.message : t('UserRoles.DeletingError'),
        );
      }
    }
  }, [modal, deleteRole, t, setDirty, navigateGrid]);

  const deleteRoleModal = useCallback(async () => {
    setIsDeleting(true);
    await modal.delete(t('UserRoles.ConfirmDelete'), 'delete-modal', () =>
      handleDeleteRole(),
    );
    setIsDeleting(false);
  }, [handleDeleteRole, modal, t]);

  const pageTitle = useMemo(() => {
    if (duplicate) return 'Duplicate';
    else if (id) return 'Edit';
    return 'Add';
  }, [duplicate, id]);

  const submitUserRoleForm = useCallback<UserRoleFormSubmitFn>(
    async (values) => {
      setIsSaving(true);
      const isExisting = id && !duplicate;

      try {
        const { body, status, ok } = isExisting
          ? await putRole(values)
          : await postUserRole(jwt, space, values);

        if (!ok) {
          throw parseResponseErrors(body, status);
        }

        toast.success(
          isExisting
            ? t('UserRoles.Updated', { roleName: body.name })
            : t('UserRoles.Added', { roleName: body.name }),
        );
        if (!isExisting) {
          navigate(buildUrlWithSpace(`user-roles/edit/${body.id}`));
        }
        return [body, {}, false];
      } catch (error) {
        if (!(error instanceof ResponseError)) {
          toast.error(t('Form.CommunicationErrorMessage'));
          return [undefined, {}, true];
        }

        if (!error.errors || !Object.keys(error.errors).length) {
          toast.error(
            isExisting
              ? t('UserRoles.UpdatingError')
              : t('UserRoles.AddingError'),
          );
          return [undefined, {}, true];
        }

        return [
          undefined,
          typeof error.errors === 'string'
            ? { '': error.errors }
            : error.errors,
          true,
        ];
      } finally {
        setIsSaving(false);
      }
    },
    [buildUrlWithSpace, duplicate, id, jwt, navigate, putRole, space, t],
  );

  const showForm = useMemo(
    () => isAdmin && !firstLoading && (role || !id),
    [isAdmin, firstLoading, role, id],
  );

  const noData = useMemo(() => {
    if (showForm) return null;
    if (firstLoading) {
      return (
        <Loader
          size="small"
          type="spinner-grid"
          {...getTestProps(testId, 'loader', 'testId')}
        />
      );
    }
    if (!isAdmin) {
      toast.error(t('Global.NoAccess'));
    }
    return (
      <Heading
        level={2}
        additionalClasses={twMerge(
          'text-3xl md:text-4xl leading-8 dark:text-slate-200',
        )}
      >
        <div
          className="flex flex-col items-center justify-center text-center"
          {...getTestProps(testId, 'empty-data')}
        >
          <WarningIcon
            className="text-red w-14 md:w-20 mb-3"
            title={t('UserRoles.CouldntFetch')}
          />
          {status === 404 || !isAdmin
            ? t('Global.NoAccess')
            : t('UserRoles.CouldntFetch')}
        </div>
      </Heading>
    );
  }, [showForm, firstLoading, isAdmin, t, status]);

  const filteredContentTypes = useMemo(() => {
    if (!contentTypes) return;

    const options = contentTypes.filter(
      (ctd) =>
        !ctd.internal ||
        ctd.name === '_media' ||
        ctd.name === '_tag' ||
        ctd.name === '_plugin_settings' ||
        ctd.name === '_webhooks',
    );

    return options;
  }, [contentTypes]);

  return (
    <PageLayout
      page="userRoles"
      title={t(`UserRoles.${pageTitle}`, { roleName: role?.name })}
      buttonsDisabled={isSaving || isDeleting}
      breadcrumbs={
        <TopbarBreadcrumbs
          parentTitle={t('Global.UserRoles')}
          parentLink={gridLink}
        />
      }
      buttons={
        <>
          <TopbarCancelButton isDirty={formState?.dirty} link={gridLink} />
          {isAdmin && (
            <>
              <TopbarSaveButton
                form="role-form"
                isLoading={isSaving}
                navigateOnSave={navigateOnSave}
                disabled={!spaceLimits?.user_roles_enabled}
              />
              <TopbarActionMenu>
                {id && !duplicate && (
                  <TopbarDuplicateButton
                    link={buildUrlWithSpace(`user-roles/duplicate/${id}`)}
                    disabled={!spaceLimits?.user_roles_enabled}
                  />
                )}
                <TopbarSaveAndLeaveButton
                  form="role-form"
                  navigateOnSave={navigateOnSave}
                  disabled={!spaceLimits?.user_roles_enabled}
                />
                {id && !duplicate && (
                  <TopbarDeleteButton onClick={deleteRoleModal} />
                )}
              </TopbarActionMenu>
            </>
          )}
        </>
      }
      testId={testId}
    >
      {showForm ? (
        <div className={predefinedLayoutClasses.withSidebar}>
          <div className={predefinedLayoutClasses.leftColumnWhite}>
            <UserRoleForm
              key={pageTitle}
              role={role}
              onSave={submitUserRoleForm}
              contentTypes={filteredContentTypes}
              disabled={
                isSaving || isDeleting || !spaceLimits?.user_roles_enabled
              }
              navigateOnSave={navigateOnSave}
              duplicate={duplicate}
              setFormState={setFormState}
              testId={testId}
            />
          </div>
          <div className={predefinedLayoutClasses.rightColumn}>
            {!spaceLimits?.user_roles_enabled && (
              <div
                className="rounded-lg p-5 w-full bg-white dark:bg-slate-950 dark:text-slate-200"
                {...getTestProps(testId, 'upgrade-plan-info')}
              >
                <div
                  className={
                    'w-full flex flex-wrap gap-x-2 gap-y-1 items-center justify-between ' +
                    'mr-2 font-bold text-base dark:text-slate-200 mb-2'
                  }
                >
                  {t('UserRoles.CustomRoles')}
                  {spaceLimits?.price !== -1 && (
                    <LinkButton link={`/space/upgrade/${space}`}>
                      {t('Global.UpgradePlan')}
                    </LinkButton>
                  )}
                </div>
                <p className="text-red font-bold mb-3">
                  {t('UserRoles.Disabled')}
                </p>
                {t('UserRoles.CustomRolesUpgrade')}
              </div>
            )}
            <div className="rounded-lg p-5 w-full bg-white dark:bg-slate-950 dark:text-slate-200">
              <p className="font-bold text-red mb-3">{t('Global.Warning')}</p>
              {t('UserRoles.Information')}
            </div>
          </div>
        </div>
      ) : (
        <div className={predefinedLayoutClasses.whiteBox}>{noData}</div>
      )}
    </PageLayout>
  );
};

export default AddUserRole;
